import React, { useState, useEffect, useCallback } from "react";
import { Card, Table, Button, Tag, Typography, Flex, message } from "antd";
import { PlusOutlined, CheckOutlined, PhoneOutlined, MailOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getCookie } from "../../../utils/cookieUtils";
import { WE_CRM_URL } from "../../../config/api";
import { getCompanySettings } from "../../../services/organizationService";
import { getClientOnboardRequests, approveStartupDoctorEntity } from "./startupDoctorService";
import StartupDoctorModal from "./StartupDoctorModal";

const { Text } = Typography;

// Helper to format capital/camelCase string to Title Case
function toTitleCase(str) {
  if (!str) return "—";
  return String(str)
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function ClientOnboardRequestsSection() {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weCrmUrl, setWeCrmUrl] = useState(WE_CRM_URL || "https://aistartupdoctor.com/api");
  const [weCrmCompanyId, setWeCrmCompanyId] = useState(null);
  const [updatingLeadId, setUpdatingLeadId] = useState(null);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Load Settings for WE_CRM_URL and WE_CRM_COMPANYID
  useEffect(() => {
    if (!effectiveCompanyCode) return;
    getCompanySettings(effectiveCompanyCode)
      .then((res) => {
        if (res?.success && res.settings) {
          if (res.settings.weCrmUrl) setWeCrmUrl(res.settings.weCrmUrl);
          if (res.settings.weCrmCompanyId) setWeCrmCompanyId(res.settings.weCrmCompanyId);
        }
      })
      .catch((err) => console.warn("Could not load WE-CRM settings:", err));
  }, [effectiveCompanyCode]);

  // Fetch Onboard Requests
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getClientOnboardRequests({
        weCrmUrl,
        companyId: weCrmCompanyId || undefined,
        companyCode: effectiveCompanyCode || undefined,
      });
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching CRM onboard requests:", err);
      message.error(err.message || "Failed to load client onboard requests.");
    } finally {
      setLoading(false);
    }
  }, [weCrmUrl, weCrmCompanyId, effectiveCompanyCode]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Open modal
  const handleOpenModal = (reqItem) => {
    setSelectedRequest(reqItem);
    setShowModal(true);
  };

  // Approve Entity Request
  const handleApproveEntity = async (reqItem) => {
    if (!weCrmUrl) {
      message.error("Startup Doctor URL is not configured.");
      return;
    }

    setUpdatingLeadId(reqItem._id);
    try {
      await approveStartupDoctorEntity({
        weCrmUrl,
        userId: reqItem._id,
        requestId: reqItem.entityRequestId,
      });
      message.success("Entity approved successfully!");
      fetchRequests();
    } catch (err) {
      console.error("Approve Entity Error:", err);
      message.error(err.message || "Failed to approve entity request.");
    } finally {
      setUpdatingLeadId(null);
    }
  };

  // Ant Design Columns
  const columns = [
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      width: 280,
      render: (_, record) => {
        const rawName = record.company_name || record.companyName || "—";
        const bType = record.business_type || record.companyType;
        let bColor = "default";
        if (bType?.includes("Private") || bType?.includes("Pvt")) bColor = "blue";
        else if (bType?.includes("LLP")) bColor = "cyan";
        else if (bType?.includes("Proprietor")) bColor = "orange";
        else if (bType?.includes("Individual")) bColor = "purple";

        return (
          <Flex vertical gap={4}>
            <Text strong style={{ color: "#0f172a", fontSize: "0.92rem", lineHeight: 1.3 }}>
              {toTitleCase(rawName)}
            </Text>
            <Flex align="center" gap={6} wrap="wrap">
              {bType && (
                <Tag color={bColor} style={{ borderRadius: 4, fontSize: "0.72rem", padding: "0 6px", margin: 0 }}>
                  {bType}
                </Tag>
              )}
              {record.state && (
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  • {toTitleCase(record.state)}
                </Text>
              )}
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: "Contact Person",
      dataIndex: "owner_name",
      key: "owner_name",
      width: 180,
      render: (_, record) => {
        const rawOwner = record.owner_name || record.contactName;
        return (
          <Flex vertical gap={2}>
            <Text style={{ color: "#1e293b", fontWeight: 600, fontSize: "0.88rem" }}>{rawOwner ? toTitleCase(rawOwner) : "—"}</Text>
            <Text type="secondary" style={{ fontSize: "0.75rem" }}>
              Primary Contact
            </Text>
          </Flex>
        );
      },
    },
    {
      title: "Communication",
      key: "communication",
      width: 230,
      render: (_, record) => {
        const phone = record.phone || record.contactNumber;
        const email = record.email;
        if (!phone && !email) return <Text type="secondary">—</Text>;

        return (
          <Flex vertical gap={4}>
            {phone && (
              <Flex align="center" gap={6}>
                <PhoneOutlined style={{ color: "#10b981", fontSize: "0.8rem" }} />
                <Text copyable={{ tooltips: ["Copy phone", "Copied!"] }} style={{ fontSize: "0.82rem", color: "#334155", whiteSpace: "nowrap" }}>
                  {phone}
                </Text>
              </Flex>
            )}
            {email && (
              <Flex align="center" gap={6}>
                <MailOutlined style={{ color: "#3b82f6", fontSize: "0.8rem" }} />
                <Text copyable={{ tooltips: ["Copy email", "Copied!"] }} ellipsis style={{ maxWidth: 170, fontSize: "0.8rem", color: "#64748b" }}>
                  {email}
                </Text>
              </Flex>
            )}
          </Flex>
        );
      },
    },
    {
      title: "Directors",
      dataIndex: "director_count",
      key: "director_count",
      align: "center",
      width: 110,
      render: (_, record) => {
        const count = Number(record.director_count || record.directorCount || 0);
        if (count === 0) return <Text type="secondary">—</Text>;
        return (
          <Tag color="purple" style={{ borderRadius: 4, fontWeight: 500, margin: 0 }}>
            {count} {count === 1 ? "Director" : "Directors"}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: 140,
      render: (_, record) => {
        const isUpdating = updatingLeadId === record._id;
        if (!record.isEntityRequest) {
          return (
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal(record)}
              loading={isUpdating}
              style={{
                borderRadius: 6,
                backgroundColor: "#2459d3",
                borderColor: "#2459d3",
                fontWeight: 500,
                fontSize: "0.82rem",
                height: 30,
                padding: "0 14px",
              }}
            >
              Onboard
            </Button>
          );
        }

        return (
          <Button
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleApproveEntity(record)}
            loading={isUpdating}
            style={{
              borderRadius: 6,
              backgroundColor: "#ecfdf5",
              color: "#059669",
              borderColor: "#a7f3d0",
              fontWeight: 600,
              fontSize: "0.82rem",
              height: 30,
              padding: "0 14px",
            }}
          >
            Approve
          </Button>
        );
      },
    },
  ];

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 14,
        boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        background: "#ffffff",
      }}
    >
      <Table
        columns={columns}
        dataSource={requests}
        rowKey={(record, idx) => record._id || String(idx)}
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} requests`,
        }}
        scroll={{ x: 880 }}
        size="middle"
        locale={{
          emptyText: "No client onboard requests found.",
        }}
      />

      {/* Startup Doctor Complete Onboarding Modal */}
      <StartupDoctorModal
        visible={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedRequest(null);
        }}
        requestData={selectedRequest}
        weCrmUrl={weCrmUrl}
        weCrmCompanyId={weCrmCompanyId}
        onSuccess={fetchRequests}
      />
    </Card>
  );
}

export default ClientOnboardRequestsSection;
